import * as React from 'react'

import {
  Box,
  CheckCircle,
  Colors,
  Icon,
  Text,
  Touchable,
} from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import { BankingValueObjectsCardTypeEnum } from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import UpgradePlanModal, { UpgradeOrigin } from 'modules/expenses/components/UpgradePlanModal'
import { useCardsCreation } from 'modules/expenses/hooks/useCardsCreationLimits'
import { useExpenses } from 'modules/expenses/pages/ExpensesContext'

import { CreateCardForm } from './PageContents/NewCard/useCardForm'

interface CardConfig {
  title: string
  description: string
  value: BankingValueObjectsCardTypeEnum
  color: Colors
  backgroundColor: Colors
}

interface CardTypeSelectorProps {
  form: FormikProps<CreateCardForm>
}

const CardTypeSelector = ({ form }: CardTypeSelectorProps) => {
  const { featureAvailabilities, data } = useExpenses()

  const configs: CardConfig[] = [
    {
      value: 'single_use',
      title: i18n.t('expenses_v2.cards.type.single_use'),
      description: i18n.t('expenses_v2.cards.description.single_use'),
      color: 'viridian1000',
      backgroundColor: 'viridian1000',
    },
    {
      value: 'virtual',
      title: i18n.t('expenses_v2.cards.type.virtual'),
      description: i18n.t('expenses_v2.cards.description.virtual'),
      color: 'radicalRed1000',
      backgroundColor: 'radicalRed1000',
    },
    {
      value: 'physical',
      title: i18n.t('expenses_v2.cards.type.physical'),
      description: i18n.t('expenses_v2.cards.description.physical'),
      color: 'grey100',
      backgroundColor: 'grey000White',
    },
  ]
  const { canCreateCard } = useCardsCreation({
    packagingPaywallsEnabled: featureAvailabilities.packagingPaywallsEnabled,
    expensesV2AddonEnabled: featureAvailabilities.expensesV2AddonEnabled,
    subscription: data?.expenses.expensesSubscriptionsConnection.nodes[0],
  })
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false)
  const [cardAction, setCardAction] = React.useState<UpgradeOrigin>('createPhysicalCard')

  const onSelectCardType = (cardType: BankingValueObjectsCardTypeEnum) => {
    if (canCreateCard(cardType)) {
      form.setFieldValue('cardType', cardType, true)
      track(trackingEvents.expenses.editNewCardtype, { context: 'company', card_type: cardType })
    } else {
      openUpgradeModal(cardType)
    }
  }
  const openUpgradeModal = (cardType: BankingValueObjectsCardTypeEnum) => {
    setCardAction(cardType === 'physical' ? 'createPhysicalCard' : 'createVirtualCard')
    setShowUpgradeModal(true)
  }
  return (
    <>
      <Box alignItems="stretch" flexDirection="row" gap="s24" width="full" justifyContent="center">
        {configs.map((config, index) => (
          <CardType
            config={config}
            isSelected={form.values.cardType === config.value}
            onSelectCardType={onSelectCardType}
            key={index}
          />
        ))}
      </Box>
      {showUpgradeModal && (
        <UpgradePlanModal
          origin={cardAction}
          onClose={() => {
            setShowUpgradeModal(false)
          }}
        />
      )}
    </>
  )
}

interface CardTypeProps {
  config: CardConfig
  isSelected: boolean
  onSelectCardType: (cardType: BankingValueObjectsCardTypeEnum) => void
}

const CardType = ({ config, isSelected, onSelectCardType }: CardTypeProps) => {
  const borderColor = isSelected ? 'viridian1000' : 'grey400'

  const setValues = () => {
    onSelectCardType(config.value)
  }

  return (
    <CardTypeWrapper config={config} setValues={setValues}>
      <Box
        alignItems="stretch"
        border={{
          all: {
            color: borderColor,
            width: 's2',
            style: 'solid',
          },
        }}
        borderRadius={{ all: 'abs008' }}
        gap="s12"
        height="full"
        justifyContent="flexStart"
        padding="s16"
        textAlign="left"
        width="full"
      >
        <Box width="full" justifyContent="spaceBetween" flexDirection="row">
          <Box paddingTop="s8">
            <CardMiniature backgroundColor={config.backgroundColor} value={config.value} />
          </Box>
          <CheckCircle checked={isSelected} disabled={false} />
        </Box>
        <Box flexDirection="row" gap="s8">
          <Text size="300" weight="medium">
            {config.title}
          </Text>
        </Box>
        <Text.Normal>{config.description}</Text.Normal>
      </Box>
    </CardTypeWrapper>
  )
}

interface CardTypeWrapperProps {
  config: CardConfig
  children: React.ReactNode
  setValues: () => void
}

const CardTypeWrapper = ({ children, setValues }: CardTypeWrapperProps) => {
  return (
    <Box width="s240">
      <Touchable expand onClick={setValues}>
        {children}
      </Touchable>
    </Box>
  )
}

interface CardProps {
  value: BankingValueObjectsCardTypeEnum
  backgroundColor: Colors
}

const CardMiniature = ({ value, backgroundColor }: CardProps) => {
  return (
    <Box paddingBottom="s16">
      <Box
        background={backgroundColor}
        borderRadius={{ all: 'abs008' }}
        boxShadow="s400"
        width="s96"
        height="s64"
        padding="s12"
        flexDirection="columnReverse"
      >
        <Box flexDirection="rowReverse">
          <Icon.Large
            icon="logo-avatar"
            color={value === 'physical' ? 'grey1000' : 'grey000White'}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default CardTypeSelector
